// Attaching a file to a chat message.
//
// Three kinds, sent two ways, and which is which is forced by the agent rather
// than chosen here.
//
// *Text* is inlined into the prompt text. The agent lowers every `prompt.files`
// entry to a media part, and `lowerMedia` in the openai-chat protocol — the one
// our gateway route reuses — accepts only png/jpeg/gif/webp and emits an
// `image_url`. A text/plain media part is therefore a hard request failure, not
// a skipped part. There is no upload endpoint in agent, cloud or gateway either,
// and the agent's fs API is read-only.
//
// *Structured documents* become text too: Word, PowerPoint, OpenDocument,
// spreadsheets and PDFs are routed to lazy readers. Embedded media is omitted;
// ordered prose, tables, sheets, formulas, slides, notes and page boundaries are
// retained because those are what the model can act on.
//
// *Images* go in `prompt.files` as a data URI, which works end to end: the
// protocol lowers it, the proxy forwards the body untouched, and the backing
// model reads it — verified against Fireworks, which named the colour of a test
// PNG.
//
// Two budgets in two currencies, because bytes on disk and characters sent stop
// being the same thing once a .docx is involved: a 1.5 MB Word file is routinely
// 46 KB of text and 1.4 MB of embedded pictures. Raw bytes protect the browser;
// characters protect the conversation, and characters are the one that matters.
// The text is re-inlined on EVERY turn, since history is re-lowered each time,
// so an attachment is paid for again with every later message. There is also no
// safety net — auto-compaction exists in the agent but stays inert while our
// model declares no context window, so an over-context session fails and keeps
// failing. Nothing server-side caps any of this. The browser is the only place a
// limit exists.

import type JSZipType from "jszip"
import {
  MAX_STRUCTURED_BYTES,
  guardZipDecompression,
  readOpenDocument,
  readPdf,
  readSpreadsheet,
} from "./structuredAttachments"

export interface TextAttachment {
  kind: "text"
  id: string
  name: string
  // The raw file size, so the id stays stable and dedupe keeps working. For a
  // Word or PowerPoint file this is much larger than the text pulled out of it,
  // which is why `chars` is tracked separately.
  size: number
  chars: number
  text: string
}

export interface ImageAttachment {
  kind: "image"
  id: string
  name: string
  size: number
  mime: string
  dataUrl: string
  width: number
  height: number
}

export type Attachment = TextAttachment | ImageAttachment

// Raw ceiling for one plain-text file. Structured office/PDF files use the
// separate 20 MiB limit in structuredAttachments.ts.
export const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024

// The real budget: extracted text, summed across every text attachment on the
// message. ~200k characters is ~50k tokens at the four-characters-per-token
// convention this repo already uses in context-hub/embedding.ts.
export const MAX_ATTACHMENT_CHARS = 200_000

export const MAX_IMAGE_BYTES = 1_500 * 1024

// Images only, and named for it. An Office file's raw bytes never reach the
// wire — only its extracted text does — so counting a 4 MB deck against the
// same total as an image would refuse a deck plus a screenshot for no reason.
export const MAX_TOTAL_IMAGE_BYTES = 4 * 1024 * 1024

export const MAX_ATTACHMENTS = 10

// The longest edge an image is reduced to. Above roughly this size the vision
// models downsample internally anyway, so more pixels cost bytes and tokens and
// buy no detail.
export const MAX_IMAGE_EDGE = 1568

const NUL = "\u0000"
const REPLACEMENT = "\ufffd"

// Everything in C0 plus DEL, except tab, newline and carriage return.
const CONTROL_CHARS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g

// Vertical tab and form feed are how some generators write a line break.
const VERTICAL_BREAKS = /[\u000b\u000c]/g

// Exactly the four the protocol's IMAGE_MIMES allows. Other browser-decodable
// image types are normalised to WebP before they reach that allow-list.
const IMAGE_MIMES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"])

// So a refusal names the format the way a person would. Anything not here is
// reported by its mime, which is still better than "an unknown format".
const NAMED_IMAGE_KINDS: Record<string, string> = {
  "image/heic": "HEIC",
  "image/heif": "HEIC",
  "image/svg+xml": "SVG",
  "image/svg": "SVG",
  "image/bmp": "BMP",
  "image/tiff": "TIFF",
  "image/avif": "AVIF",
}

// `file.type` is only a hint, never the gate. Windows reports "" for the Office
// formats unless Office itself wrote the registry mapping, the macro and
// template variants are unmapped on most machines, and anything dragged out of
// Outlook or pasted arrives as "" or application/zip. The extension routes the
// file, and what is actually inside the zip decides docx from pptx.
const OFFICE_EXTENSIONS = /\.(?:docx|docm|dotx|dotm|pptx|pptm|potx|potm|ppsx|ppsm)$/i

// Listed only so the refusal can name the format instead of saying "binary".
const LEGACY_OFFICE_EXTENSIONS = /\.(?:doc|dot|ppt|pot|pps|xls|xlt)$/i

const WORD_EXTENSIONS = /\.(?:docx|docm|dotx|dotm|doc|dot)$/i

const SHEET_EXTENSIONS = /\.(?:xlsx|xlsm|xltx|xltm|xlsb|ods|fods)$/i
const OPEN_DOCUMENT_TEXT_EXTENSIONS = /\.(?:odt|odp)$/i
const PDF_EXTENSIONS = /\.pdf$/i
const IMAGE_EXTENSIONS = /\.(?:png|jpe?g|gif|webp|avif|bmp|tiff?|heic|heif|svg)$/i
const ARCHIVE_EXTENSIONS = /\.(?:7z|bz2|gz|rar|tar|tgz|xz|zip|jar|war|ear)$/i
const EXECUTABLE_EXTENSIONS = /\.(?:a|bin|class|dll|dylib|exe|o|obj|so|wasm)$/i

const OFFICE_MIMES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
  "application/vnd.ms-word.document.macroEnabled.12",
  "application/vnd.ms-word.template.macroEnabled.12",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.presentationml.template",
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
  "application/vnd.ms-powerpoint.presentation.macroEnabled.12",
  "application/vnd.ms-powerpoint.slideshow.macroEnabled.12",
  "application/msword",
  "application/vnd.ms-powerpoint",
])

const SHEET_MIMES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "application/vnd.oasis.opendocument.spreadsheet",
])

const PDF_MIMES = new Set(["application/pdf"])

const OPEN_DOCUMENT_TEXT_MIMES = new Set([
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.presentation",
])

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/") || IMAGE_EXTENSIONS.test(file.name)
}

/**
 * Whether an image may be attached for the selected model.
 *
 * Advisory. v2 does not enforce capabilities on the request path, so nothing
 * server-side would reject the image — this exists to refuse early, with a
 * reason, rather than send a picture to a model that cannot look at it.
 *
 * An empty list means "not stated", not "nothing accepted". The browser reads
 * the model over the agent's v2 API, which does not carry `modalities` through
 * to `capabilities`, so the list is routinely empty for a model that does
 * accept images — the CLI reads the same model over v1 and sees it declared
 * correctly, which is why images attached there and not here. Only an explicit
 * list that names other inputs and omits `image` is a denial.
 */
export function imageInputAllowed(input: readonly string[] | undefined): boolean {
  if (!input || input.length === 0) return true
  return input.includes("image")
}

export function isOfficeFile(file: File): boolean {
  return (
    OFFICE_EXTENSIONS.test(file.name) ||
    LEGACY_OFFICE_EXTENSIONS.test(file.name) ||
    OFFICE_MIMES.has(file.type)
  )
}

/** Characters already spent on this message, for the shared text budget. */
export function extractedChars(attachments: Attachment[]): number {
  return attachments.reduce((sum, item) => sum + (item.kind === "text" ? item.chars : 0), 0)
}

export function imageBytes(attachments: Attachment[]): number {
  return attachments.reduce((sum, item) => sum + (item.kind === "image" ? item.size : 0), 0)
}

/**
 * Reads one file, or throws a message meant to be shown to the user.
 *
 * `imagesAllowed` comes from the selected model's declared input modalities. v2
 * does not enforce capabilities — nothing server-side would reject the image —
 * but refusing here with a reason beats sending something the model cannot read.
 * `charsUsed` is what the other text attachments on this message already spend.
 */
export async function readAttachment(
  file: File,
  imagesAllowed = true,
  charsUsed = 0,
): Promise<Attachment> {
  if (isImageFile(file)) {
    if (!imagesAllowed) {
      throw new Error(`The selected model does not accept images, so ${file.name} cannot be attached.`)
    }
    return await readImageAttachment(file)
  }
  if (LEGACY_OFFICE_EXTENSIONS.test(file.name)) {
    const extension = file.name.split(".").at(-1)?.toLowerCase() ?? "Office"
    throw new Error(
      `${file.name} uses the old binary .${extension} format, which cannot be read here. ` +
        "Open it and save it in a current Office or OpenDocument format, then attach that copy.",
    )
  }
  if (SHEET_EXTENSIONS.test(file.name) || SHEET_MIMES.has(file.type)) {
    return await readStructuredAttachment(file, charsUsed, (bytes) => readSpreadsheet(bytes, file.name))
  }
  if (PDF_EXTENSIONS.test(file.name) || PDF_MIMES.has(file.type)) {
    return await readStructuredAttachment(file, charsUsed, (bytes) => readPdf(bytes, file.name))
  }
  if (OPEN_DOCUMENT_TEXT_EXTENSIONS.test(file.name) || OPEN_DOCUMENT_TEXT_MIMES.has(file.type)) {
    const format = file.name.toLowerCase().endsWith(".odp") || file.type.includes("presentation") ? "odp" : "odt"
    return await readStructuredAttachment(file, charsUsed, (bytes) => readOpenDocument(bytes, file.name, format))
  }
  if (ARCHIVE_EXTENSIONS.test(file.name)) {
    throw new Error(`${file.name} is an archive. Extract it and attach the files that matter.`)
  }
  if (EXECUTABLE_EXTENSIONS.test(file.name)) {
    throw new Error(`${file.name} is compiled or executable binary data, which this chat cannot read.`)
  }
  if (isOfficeFile(file)) return await readOfficeAttachment(file, charsUsed)
  return await readTextAttachment(file, charsUsed)
}

async function readStructuredAttachment(
  file: File,
  charsUsed: number,
  extract: (bytes: Uint8Array) => Promise<string>,
): Promise<TextAttachment> {
  if (file.size > MAX_STRUCTURED_BYTES) {
    throw new Error(
      `${file.name} is ${formatBytes(file.size)}; the limit for documents, spreadsheets and PDFs is ` +
        `${formatBytes(MAX_STRUCTURED_BYTES)} per file.`,
    )
  }
  const bytes = new Uint8Array(await file.arrayBuffer())
  const text = withinBudget(file.name, await extract(bytes), charsUsed)
  return {
    kind: "text",
    id: `${file.name}:${file.size}:${file.lastModified}`,
    name: file.name,
    size: file.size,
    chars: text.length,
    text,
  }
}

function tooManyChars(name: string, chars: number, charsUsed: number): Error {
  const remaining = MAX_ATTACHMENT_CHARS - charsUsed
  return new Error(
    `${name} holds ${chars.toLocaleString()} characters of text; the limit is ` +
      `${MAX_ATTACHMENT_CHARS.toLocaleString()} per message` +
      (charsUsed > 0 ? `, and ${remaining.toLocaleString()} are still free` : "") +
      `. Attach a shorter file, or paste in the part that matters.`,
  )
}

// Refused rather than trimmed, on purpose. The text is re-inlined on every turn,
// so a silently halved document produces confidently wrong answers about the
// missing half for the rest of the session.
function withinBudget(name: string, text: string, charsUsed: number): string {
  if (text.length > MAX_ATTACHMENT_CHARS - charsUsed) {
    throw tooManyChars(name, text.length, charsUsed)
  }
  return text
}

function tooManyBytes(name: string, size: number): Error {
  return new Error(
    `${name} is ${formatBytes(size)}; the limit is ${formatBytes(MAX_ATTACHMENT_BYTES)} per file.`,
  )
}

/**
 * Reads one text file.
 *
 * The second check refuses on raw size before anything is decoded: in UTF-8 a
 * character takes at least one byte, so a file bigger than four times the
 * character budget cannot possibly fit inside it, and there is no point reading
 * megabytes to discover that.
 */
export async function readTextAttachment(file: File, charsUsed = 0): Promise<TextAttachment> {
  if (file.size > MAX_ATTACHMENT_BYTES) throw tooManyBytes(file.name, file.size)
  if (file.size > MAX_ATTACHMENT_CHARS * 4) {
    throw tooManyChars(file.name, Math.floor(file.size / 4), charsUsed)
  }

  const bytes = new Uint8Array(await file.arrayBuffer())
  const text = withinBudget(file.name, decodeTextFile(bytes, file.name), charsUsed)

  return {
    kind: "text",
    // Identity is the file, not the pick: attaching the same file twice is a
    // duplicate to be caught, not two copies to be sent.
    id: `${file.name}:${file.size}:${file.lastModified}`,
    name: file.name,
    size: file.size,
    chars: text.length,
    text,
  }
}

// ---------------------------------------------------------------------------
// Text decoding
// ---------------------------------------------------------------------------

/**
 * Decodes a file's bytes, guessing the encoding the way an editor would.
 *
 * `file.text()` is always UTF-8, which wrongly refused two files this product
 * really does see: a CSV exported from Excel, which is windows-1252 so a single
 * smart quote makes it invalid UTF-8, and a .txt saved by Notepad as "Unicode",
 * which is UTF-16 and reads back as a string of NULs.
 *
 * The rung order is load-bearing. UTF-16 has to be sniffed BEFORE UTF-8 is
 * tried, because NUL is a perfectly legal one-byte UTF-8 sequence — so a strict
 * UTF-8 decode succeeds on BOM-less UTF-16 and hands back a string full of NULs
 * that the binary check would then reject.
 */
export function decodeTextFile(bytes: Uint8Array, name: string): string {
  const binary = () => new Error(`${name} looks like a binary file.`)

  // A BOM settles it outright, and TextDecoder strips all three.
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return checkText(decode(bytes, "utf-8", binary), true, binary)
  }
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return checkText(decode(bytes, "utf-16le", binary), false, binary)
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return checkText(decode(bytes, "utf-16be", binary), false, binary)
  }

  const wide = sniffUtf16(bytes)
  if (wide) return checkText(decode(bytes, wide, binary), false, binary)

  try {
    // `fatal` beats counting replacement characters: it fails on the first
    // ill-formed sequence rather than needing a whole percent of them.
    return checkText(new TextDecoder("utf-8", { fatal: true }).decode(bytes), true, binary)
  } catch {
    // windows-1252 maps almost every byte, so this is the last rung and whether
    // the result is text gets decided by the control-character ratio.
    return checkText(decode(bytes, "windows-1252", binary), true, binary)
  }
}

function decode(bytes: Uint8Array, encoding: string, binary: () => Error): string {
  try {
    return new TextDecoder(encoding).decode(bytes)
  } catch {
    // An encoding no browser implements, utf-32 being the only realistic one.
    throw binary()
  }
}

// Zeros at odd offsets mean UTF-16LE ASCII, at even offsets UTF-16BE. A quarter
// of the sample being NUL is far past anything a real UTF-8 text file contains.
function sniffUtf16(bytes: Uint8Array): "utf-16le" | "utf-16be" | null {
  const sample = Math.min(bytes.length, 1024)
  let odd = 0
  let even = 0
  for (let i = 0; i < sample; i++) {
    if (bytes[i] === 0) {
      if (i % 2 === 1) odd++
      else even++
    }
  }
  if (odd + even < sample * 0.25) return null
  if (odd > even * 4) return "utf-16le"
  if (even > odd * 4) return "utf-16be"
  return null
}

/**
 * The last gate before a decoded string is called text.
 *
 * Control characters are counted alongside replacement characters, and that is
 * what makes the windows-1252 rung safe: 1252 maps almost every byte, so it
 * produces no replacement characters at all, and a test looking only for those
 * would happily accept an executable.
 *
 * `nulIsBinary` is false on the two UTF-16 rungs, where a NUL code unit is
 * ordinary decoded content rather than a sign of a binary file.
 */
function checkText(text: string, nulIsBinary: boolean, binary: () => Error): string {
  if (nulIsBinary && text.includes(NUL)) throw binary()
  const controls = text.match(CONTROL_CHARS)?.length ?? 0
  const replacements = text.split(REPLACEMENT).length - 1
  if (text.length > 0 && (controls + replacements) / text.length > 0.01) throw binary()
  return text
}

// ---------------------------------------------------------------------------
// Word and PowerPoint
// ---------------------------------------------------------------------------

type OfficeFormat = "docx" | "pptx"

function sniffContainer(bytes: Uint8Array): "zip" | "ole2" | "other" {
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) return "zip"
  if (bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0) return "ole2"
  return "other"
}

/**
 * Reads a .docx or .pptx by opening the zip and pulling the words out.
 *
 * Nothing here decompresses the media: JSZip reads the central directory on load
 * and inflates only the entries actually asked for, so a 4 MB deck that is
 * 3.9 MB of pictures costs the ArrayBuffer plus a few KB of XML.
 */
export async function readOfficeAttachment(file: File, charsUsed = 0): Promise<TextAttachment> {
  if (file.size > MAX_STRUCTURED_BYTES) {
    throw new Error(
      `${file.name} is ${formatBytes(file.size)}; the limit for documents, spreadsheets and PDFs is ` +
        `${formatBytes(MAX_STRUCTURED_BYTES)} per file.`,
    )
  }

  const bytes = new Uint8Array(await file.arrayBuffer())
  const word = WORD_EXTENSIONS.test(file.name) || file.type.includes("word")
  const container = sniffContainer(bytes)

  if (container === "ole2") {
    // A password-protected .docx is also OLE2 — OOXML encryption wraps the whole
    // package in a CFB container rather than encrypting entries inside the zip —
    // so the magic alone cannot tell it from a Word 97 file. The extension can,
    // and telling someone to "save it as .docx" when it already is one would
    // send them hunting for the wrong problem.
    if (OFFICE_EXTENSIONS.test(file.name)) {
      throw new Error(
        `${file.name} is password-protected, so its text cannot be read here. ` +
          `Save a copy without the password and attach that.`,
      )
    }
    throw new Error(
      `${file.name} is in the old binary ${word ? "Word" : "PowerPoint"} format, which cannot be read here. ` +
        `Open it and save it again as .${word ? "docx" : "pptx"}, then attach that.`,
    )
  }
  if (container !== "zip") {
    throw new Error(
      `${file.name} could not be opened as a ${word ? "Word" : "PowerPoint"} file — ` +
        `it may be damaged, or renamed from another format.`,
    )
  }
  guardZipDecompression(bytes, file.name)

  const { default: JSZip } = await import("jszip")
  let zip: JSZipType
  try {
    zip = await JSZip.loadAsync(bytes)
  } catch {
    throw new Error(`${file.name} could not be opened — the file is probably damaged or incomplete.`)
  }

  const format = classify(zip, file.name)
  const extracted = format === "docx" ? await extractDocx(zip, file.name) : await extractPptx(zip, file.name)
  const text = withinBudget(file.name, extracted, charsUsed)

  return {
    kind: "text",
    id: `${file.name}:${file.size}:${file.lastModified}`,
    name: file.name,
    size: file.size,
    chars: text.length,
    text,
  }
}

// By contents rather than by name, so a mislabelled or renamed file still works
// — and so the mistakes people actually make get their own message instead of a
// generic failure.
function classify(zip: JSZipType, name: string): OfficeFormat {
  if (zip.file("word/document.xml")) return "docx"
  if (Object.keys(zip.files).some((path) => SLIDE_PART.test(path))) return "pptx"
  if (zip.file("xl/workbook.xml")) {
    throw new Error(`${name} is a spreadsheet saved with the wrong file extension. Rename it to .xlsx and attach it again.`)
  }
  if (zip.file("mimetype")) {
    throw new Error(`${name} looks like an OpenDocument file. Save it as .docx or .pptx, then attach that.`)
  }
  if (zip.file("[Content_Types].xml")) {
    throw new Error(`${name} opened, but its Word document part is missing; the file is probably damaged.`)
  }
  throw new Error(`${name} opened as a zip archive but contains no Word document or PowerPoint slides.`)
}

function noText(name: string, format: OfficeFormat): Error {
  return new Error(
    `${name} contains no text that can be read — the ${format === "docx" ? "document" : "slides"} ` +
      `may be images only. Attach a screenshot of the part you want read instead.`,
  )
}

// A shape's text is written twice, once under mc:Choice and again as VML under
// mc:Fallback, so one branch has to go before anything is read or every text box
// arrives duplicated.
const MC_FALLBACK = /<mc:Fallback>[\s\S]*?<\/mc:Fallback>/g

// Tokenising what carries text, rather than stripping the tags around it, is
// what keeps two dangerous things out.
//
// `w:instrText` holds field codes, so a document with a table of contents would
// otherwise read `TOC \o "1-3" \h` in the middle of its prose. And `w:delText`
// holds tracked-change deletions — a tag strip re-inserts text the author
// deleted, interleaved with whatever replaced it, producing sentences that were
// never written and no sign that it happened. Neither element is a `w:t`, so
// neither is matched here.
//
// It also makes pretty-printed packages safe. Word emits no whitespace between
// elements, but LibreOffice and python-docx indent, and a tag strip turns that
// indentation into ragged text inside every paragraph.
const DOCX_TOKEN =
  /<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>|<w:tab\s*\/>|<w:br\b[^>]*\/?>|<w:cr\s*\/?>|<w:noBreakHyphen\s*\/?>|<\/w:p>\s*<\/w:tc>|<\/w:tr>|<w:p\s*\/>|<\/w:p>/g

async function extractDocx(zip: JSZipType, name: string): Promise<string> {
  const body = await readEntry(zip, "word/document.xml")
  if (body === undefined) {
    throw new Error(`${name} opened, but its Word document part is missing; the file is probably damaged.`)
  }

  const parts = [xmlToText(body, DOCX_TOKEN, docxToken)]

  // Appended rather than placed inline: resolving w:footnoteReference against
  // w:footnote by id buys a model reading prose very little. The two separator
  // pseudo-footnotes carry no w:t, so they contribute nothing and need no
  // filtering.
  for (const [entry, heading] of [
    ["word/footnotes.xml", "Footnotes"],
    ["word/endnotes.xml", "Endnotes"],
  ] as const) {
    const xml = await readEntry(zip, entry)
    if (xml === undefined) continue
    const text = tidy(xmlToText(xml, DOCX_TOKEN, docxToken))
    if (text) parts.push(`--- ${heading} ---\n${text}`)
  }

  // Headers and footers are deliberately skipped: they come in triples per
  // section (default, first, even), so a three-section document would repeat
  // "Confidential — Draft" nine times and add nothing else.

  const text = tidy(parts.join("\n\n"))
  if (!text) throw noText(name, "docx")
  return text
}

function docxToken(match: string, captured: string | undefined): string {
  if (captured !== undefined) return captured
  if (match.startsWith("<w:tab")) return "\t"
  if (match.startsWith("<w:noBreakHyphen")) return "-"
  // A paragraph ending a table cell separates columns; the row ends the line.
  if (match.includes("</w:tc>")) return "\t"
  return "\n"
}

const SLIDE_PART = /^ppt\/slides\/slide(\d+)\.xml$/
const SLD_ID = /<p:sldId\b[^>]*r:id="([^"]+)"/g
const RELATIONSHIP = /<Relationship\b[^>]*Id="([^"]+)"[^>]*Target="([^"]+)"/g
const NOTES_TARGET = /Target="([^"]*notesSlides\/notesSlide\d+\.xml)"/

// Auto slide numbers and dates live in a:fld and would otherwise land in the
// text as stray digits.
const PPTX_FLD = /<a:fld\b[\s\S]*?<\/a:fld>/g
const PPTX_TOKEN = /<a:t(?:\s[^>]*)?>([\s\S]*?)<\/a:t>|<a:br\b[^>]*\/?>|<\/a:p>\s*<\/a:tc>|<\/a:tr>|<\/a:p>/g

async function extractPptx(zip: JSZipType, name: string): Promise<string> {
  const slides = await slideOrder(zip)
  if (slides.length === 0) throw new Error(`${name} contains no slides.`)

  const sections: string[] = []
  let withText = 0

  for (const [index, path] of slides.entries()) {
    const xml = await readEntry(zip, path)
    const body = xml === undefined ? "" : tidy(xmlToText(xml.replace(PPTX_FLD, ""), PPTX_TOKEN, pptxToken))
    const notes = await slideNotes(zip, path)
    if (body || notes) withText++
    // Numbering is the position in the deck, which is what the reader sees on
    // screen. Every slide gets a heading, including a text-free one, or "slide
    // 4" in an answer would mean a different slide than in the deck.
    sections.push(
      [`## Slide ${index + 1}`, body || "(no text on this slide)", notes ? `### Notes\n${notes}` : ""]
        .filter(Boolean)
        .join("\n"),
    )
  }

  if (withText === 0) throw noText(name, "pptx")
  return sections.join("\n\n").trim()
}

function pptxToken(match: string, captured: string | undefined): string {
  if (captured !== undefined) return captured
  if (match.includes("</a:tc>")) return "\t"
  return "\n"
}

/**
 * Slide parts in deck order.
 *
 * File names are not deck order. PowerPoint keeps slide7.xml named slide7 after
 * it is dragged to the front, and deleting a slide leaves a gap — so on any
 * edited deck a numeric sort of the file names is simply a different order from
 * the one on screen. The real order lives in p:sldIdLst, resolved through the
 * presentation's relationships, which also drops the orphan slide parts some
 * generators leave behind after a delete.
 */
async function slideOrder(zip: JSZipType): Promise<string[]> {
  const presentation = await readEntry(zip, "ppt/presentation.xml")
  const rels = await readEntry(zip, "ppt/_rels/presentation.xml.rels")

  if (presentation !== undefined && rels !== undefined) {
    const targets = new Map<string, string>()
    for (const match of rels.matchAll(RELATIONSHIP)) {
      targets.set(match[1]!, resolveFrom("ppt", match[2]!))
    }
    const ordered: string[] = []
    for (const match of presentation.matchAll(SLD_ID)) {
      const path = targets.get(match[1]!)
      if (path && zip.file(path)) ordered.push(path)
    }
    if (ordered.length > 0) return ordered
  }

  // Fallback, numeric because a string sort puts slide10 before slide2.
  return Object.keys(zip.files)
    .filter((path) => SLIDE_PART.test(path))
    .sort((a, b) => Number(SLIDE_PART.exec(a)![1]) - Number(SLIDE_PART.exec(b)![1]))
}

/**
 * A slide's speaker notes, found through the slide's own relationships.
 *
 * Correlating notesSlideN to slideN by number is wrong the moment a slide has no
 * notes: a deck where only slides 2 and 3 have notes contains notesSlide1 and
 * notesSlide2, so matching by number gives slide 2's notes to slide 1 and
 * misinforms the model with nothing to show it happened. Partial notes are the
 * norm, not the corner case.
 */
async function slideNotes(zip: JSZipType, slidePath: string): Promise<string> {
  const file = slidePath.slice(slidePath.lastIndexOf("/") + 1)
  const rels = await readEntry(zip, `ppt/slides/_rels/${file}.rels`)
  if (rels === undefined) return ""
  const target = NOTES_TARGET.exec(rels)?.[1]
  if (!target) return ""
  const xml = await readEntry(zip, resolveFrom("ppt/slides", target))
  if (xml === undefined) return ""
  return tidy(xmlToText(xml.replace(PPTX_FLD, ""), PPTX_TOKEN, pptxToken))
}

// Relationship targets are relative to the part's own directory.
function resolveFrom(base: string, target: string): string {
  if (target.startsWith("/")) return target.slice(1)
  const out: string[] = []
  for (const segment of [...base.split("/"), ...target.split("/")]) {
    if (segment === "" || segment === ".") continue
    if (segment === "..") out.pop()
    else out.push(segment)
  }
  return out.join("/")
}

async function readEntry(zip: JSZipType, path: string): Promise<string | undefined> {
  const entry = zip.file(path)
  if (!entry) return undefined
  return await entry.async("string")
}

/**
 * Runs the tokens over one XML part.
 *
 * Adjacent runs are joined with nothing between them, and that is the only
 * correct choice: Word and PowerPoint split words mid-token constantly — a
 * spell-check boundary, a language change or one bold letter yields
 * `<w:t>Hel</w:t><w:t>lo</w:t>` — so any separator at all would corrupt ordinary
 * prose. Nothing may trim an individual run either, since a run holding a single
 * space is how the gap between two words is stored, and `xml:space="preserve"`
 * is only a signal that consumers must not normalise it away.
 */
function xmlToText(
  xml: string,
  token: RegExp,
  map: (match: string, captured?: string) => string,
): string {
  const out: string[] = []
  for (const match of xml.replace(MC_FALLBACK, "").matchAll(token)) {
    out.push(map(match[0], match[1]))
  }
  return decodeXmlEntities(out.join(""))
}

const NAMED_ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" }
const ENTITY = /&(?:#(\d{1,7})|#[xX]([0-9a-fA-F]{1,6})|(amp|lt|gt|quot|apos));/g

/**
 * One pass, deliberately.
 *
 * Any decode-then-decode sequence turns `&amp;lt;` into `<` rather than the
 * literal `&lt;` the document actually says, whichever order the steps run in. A
 * single alternation consumes each reference exactly once. The digit caps stop a
 * pathological `&#99999999999;` from reaching parseInt.
 */
export function decodeXmlEntities(text: string): string {
  return text.replace(
    ENTITY,
    (match, dec: string | undefined, hex: string | undefined, name: string | undefined) => {
      if (name) return NAMED_ENTITIES[name] ?? match
      const code = dec ? Number.parseInt(dec, 10) : Number.parseInt(hex ?? "", 16)
      // A lone surrogate or an out-of-range code point would make an ill-formed
      // string that breaks JSON serialisation on the way to the agent.
      if (!Number.isFinite(code) || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) {
        return REPLACEMENT
      }
      if (code === 0) return ""
      return String.fromCodePoint(code)
    },
  )
}

// Runs after entity decoding, so `&#0;` cannot smuggle a NUL past it — and the
// vertical breaks are turned into newlines before the strip removes them.
function tidy(text: string): string {
  return text
    .replace(VERTICAL_BREAKS, "\n")
    .replace(CONTROL_CHARS, "")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------

/**
 * Reads one image, downscaling and re-encoding only when it has to.
 *
 * An image already inside both budgets is sent byte for byte, so a PNG
 * screenshot of small text stays lossless — that being both the likeliest thing
 * to be pasted here and the thing lossy re-encoding hurts most.
 */
export async function readImageAttachment(file: File): Promise<ImageAttachment> {
  const mime = file.type.split(";", 1)[0]?.trim().toLowerCase() ?? ""
  const decoded = await decodeBrowserImage(file).catch(() => {
    const kind = NAMED_IMAGE_KINDS[mime] ?? mime
    throw new Error(
      `${file.name} could not be decoded as ${kind || "an image"} by this browser. ` +
        "Convert it to PNG, JPEG or WebP and attach that copy.",
    )
  })

  const id = `${file.name}:${file.size}:${file.lastModified}`
  try {
    // Only protocol-native formats travel byte for byte. Anything else that the
    // browser can decode is normalised to WebP below before it reaches the
    // agent's four-format image allow-list.
    const fits =
      IMAGE_MIMES.has(mime) &&
      file.size <= MAX_IMAGE_BYTES &&
      Math.max(decoded.width, decoded.height) <= MAX_IMAGE_EDGE
    if (fits) {
      return {
        kind: "image",
        id,
        name: file.name,
        size: file.size,
        mime,
        dataUrl: await blobToDataUrl(file),
        width: decoded.width,
        height: decoded.height,
      }
    }

    const encoded = await downscale(decoded.source, decoded.width, decoded.height, file.name)
    return {
      kind: "image",
      id,
      name: file.name,
      size: encoded.blob.size,
      mime: encoded.mime,
      dataUrl: await blobToDataUrl(encoded.blob),
      width: encoded.width,
      height: encoded.height,
    }
  } finally {
    decoded.close()
  }
}

type DecodedImage = {
  source: CanvasImageSource
  width: number
  height: number
  close: () => void
}

async function decodeBrowserImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file)
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() }
    } catch {
      // Safari and some Chromium codecs can display a format through <img>
      // without exposing it through createImageBitmap. Try the browser's other
      // native decoder before reporting the file as unsupported.
    }
  }

  const url = URL.createObjectURL(file)
  const image = new Image()
  image.decoding = "async"
  image.src = url
  try {
    await image.decode()
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    }
  } catch (error) {
    URL.revokeObjectURL(url)
    throw error
  }
}

// A ladder rather than one shot: a photo at 1568px is usually well inside budget
// at 0.85, and only a dense screenshot needs to go lower.
const QUALITIES = [0.85, 0.7, 0.55]

async function downscale(source: CanvasImageSource, sourceWidth: number, sourceHeight: number, name: string) {
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(sourceWidth, sourceHeight))
  const width = Math.max(1, Math.round(sourceWidth * scale))
  const height = Math.max(1, Math.round(sourceHeight * scale))

  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  if (!context) throw new Error(`${name} could not be resized: no 2D canvas is available.`)
  context.drawImage(source, 0, 0, width, height)

  // WebP rather than the original format: it is in the protocol's allow-list,
  // Fireworks decodes it, and it is markedly smaller than PNG for a screenshot.
  // An animated GIF keeps only its first frame through a canvas — the model only
  // ever saw one frame, so what is lost is in the chip, not the request.
  for (const quality of QUALITIES) {
    const blob = await toBlob(canvas, "image/webp", quality)
    if (blob && blob.size <= MAX_IMAGE_BYTES) return { blob, mime: "image/webp", width, height }
  }

  throw new Error(
    `${name} is still over ${formatBytes(MAX_IMAGE_BYTES)} after resizing. Crop it or save it smaller first.`,
  )
}

function toBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, mime, quality))
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error("Could not read the file."))
    reader.readAsDataURL(blob)
  })
}

// ---------------------------------------------------------------------------
// Composing and rendering
// ---------------------------------------------------------------------------

export interface PromptFile {
  uri: string
  name: string
}

/**
 * The two halves of what gets sent: the text with every text attachment inlined,
 * and the images as prompt files.
 *
 * Attachments lead the text because the question is usually about them. The
 * escaping matters — a file whose body contains the closing tag, or a filename
 * containing a quote, would otherwise make the transcript unparseable on the way
 * back. `mime` is deliberately absent from the files: the agent infers it from
 * the data URI, and a second copy invites the two disagreeing, which
 * validateMedia treats as a hard failure.
 */
export function composeMessage(
  text: string,
  attachments: Attachment[],
): { text: string; files: PromptFile[] } {
  const blocks = attachments
    .filter((attachment): attachment is TextAttachment => attachment.kind === "text")
    .map((attachment) => {
      const name = attachment.name.replace(/"/g, "'")
      const body = attachment.text.replace(/<\/attachment/g, "<\\/attachment")
      return `<attachment name="${name}">\n${body}\n</attachment>`
    })

  const files = attachments
    .filter((attachment): attachment is ImageAttachment => attachment.kind === "image")
    .map((attachment) => ({ uri: attachment.dataUrl, name: attachment.name }))

  const composed =
    blocks.length === 0 ? text : text ? `${blocks.join("\n\n")}\n\n${text}` : blocks.join("\n\n")
  return { text: composed, files }
}

export interface ParsedAttachment {
  name: string
  body: string
}

/**
 * The inverse of composeMessage's text half, for rendering.
 *
 * Nothing is stored about a text attachment other than the text itself, so this
 * is the only record of one after a reload — and one code path therefore serves
 * both the live echo and reloaded history. Images need none of this; they come
 * back as `message.files`.
 */
export function parseAttachments(text: string): { blocks: ParsedAttachment[]; text: string } {
  const pattern = /<attachment name="([^"]*)">\n([\s\S]*?)\n<\/attachment>\n*/g
  const blocks: ParsedAttachment[] = []
  const rest = text.replace(pattern, (_match, name: string, body: string) => {
    blocks.push({ name, body: body.replace(/<\\\/attachment/g, "</attachment") })
    return ""
  })
  return { blocks, text: rest.trim() }
}
